















































































































import { Component, Prop, Mixins } from "vue-property-decorator";
import _ from "@/boot/lodash";
import { DataModules } from "@/store/modules/data";
import HasValidation from "@/mixins/HasValidation";
import { IAffiliatePayoutDestinationsMethods } from "@/models/providers/affiliatePayoutDestinations";
import {
  IAffiliatePayoutDestination,
  IAffiliatePayoutDestinationDynamicField,
  IAffiliateBrandPayoutDestination
} from "@/models/affiliate";
import { AffiliatePayoutDestinationCode } from "@/data/enums/affiliates";
import { ValidationObserver } from "vee-validate/dist/types/components/Observer.d";
import { DynamicSettingsFieldTypes } from "@/data/enums/fields";
import AdminBrandId from "@/mixins/adminBrandId";

type AddEditPayoutFormType = Partial<
  Pick<
    IAffiliatePayoutDestination,
    "id" | "name" | "default" | "visible_for_clients"
  > & { code?: AffiliatePayoutDestinationCode }
>;

type AvailablePayoutDestinationsType = {
  [key in AffiliatePayoutDestinationCode]: IAffiliatePayoutDestination;
};

@Component({
  components: {
    "dynamic-category-field": () =>
      import("@/components/app/admin/settings/dynamicCategoryField.vue")
  }
})
export default class AddEditAffiliatePayoutDestinationModal extends Mixins(
  HasValidation,
  AdminBrandId
) {
  // Refs
  public declare $refs: {
    name: HTMLElement;
    observer: InstanceType<typeof ValidationObserver>;
  };

  // Props
  @Prop({ default: null })
  public payoutDestination!: IAffiliateBrandPayoutDestination;

  @Prop({ default: false })
  public isDisabled!: boolean;

  @Prop({ required: true })
  public createPayoutDestination!: IAffiliatePayoutDestinationsMethods["createPayoutDestination"];

  @Prop({ required: true })
  public updatePayoutDestination!: IAffiliatePayoutDestinationsMethods["updatePayoutDestination"];

  // Data
  public isProcessing = false;
  public isLoading = true;
  public availablePayoutDestinations: AvailablePayoutDestinationsType = {} as AvailablePayoutDestinationsType;
  public payoutDestinationDB: IAffiliateBrandPayoutDestination | null = null;

  public form: AddEditPayoutFormType = {
    id: undefined,
    name: "",
    default: false,
    visible_for_clients: true,
    code: AffiliatePayoutDestinationCode.WALLET
  };

  public formClone: AddEditPayoutFormType = _.cloneDeep(this.form);

  // Lifecycle methods

  public created() {
    this.loadData();
  }

  // Computed

  public get availablePayoutDestinationsArray() {
    return this.$_.toArray(this.availablePayoutDestinations);
  }
  public get selectedDestination(): IAffiliatePayoutDestination | null {
    return this.availablePayoutDestinations[this.form.code || "NO_CODE"];
  }

  public get dynamicFieldsMap() {
    return this.$_.map(
      this.selectedDestination?.meta || [],
      (
        config: IAffiliatePayoutDestinationDynamicField,
        key: AffiliatePayoutDestinationCode
      ) => {
        return {
          fieldName: key,
          isRequired: config?.validation?.includes("required"),
          isDisabled: this.isProcessing,
          field: {
            id: key,
            name: config?.name,
            field_type:
              config?.field_type === "dropdown"
                ? DynamicSettingsFieldTypes.SELECT
                : config?.field_type === "number"
                ? DynamicSettingsFieldTypes.NUMBER
                : config?.field_type === "input"
                ? DynamicSettingsFieldTypes.TEXT
                : DynamicSettingsFieldTypes.TEXT,
            options: config?.options
          }
        };
      }
    );
  }

  public get title() {
    return this.isNewDestination
      ? this.$t("_.add_payout_destination")
      : this.$t("_.update_payout_destination");
  }

  public get isNewDestination() {
    return _.isNil(this.payoutDestination?.id);
  }

  public get formIsDisabled() {
    return (
      this.isDisabled ||
      this.isLoading ||
      this.isProcessing ||
      this.hasErrors ||
      !this.formIsChanged
    );
  }

  public get formIsChanged() {
    return !this.$_.isEqual(this.form, this.formClone);
  }

  /**
   * Remove dynamic fields from previous value before setting new value
   */
  public onDestinationChanged(newValue: AffiliatePayoutDestinationCode) {
    const oldValue = this.form.code;
    if (oldValue) {
      const keysToRemove = this.$_.keys(
        this.availablePayoutDestinations?.[oldValue]?.meta
      );
      keysToRemove.forEach(key => delete this.form[key]);
    }

    this.form.code = newValue;
  }

  // Methods

  public async loadData() {
    try {
      this.isLoading = true;
      this.availablePayoutDestinations = await this.getAvailablePayoutDestinations();
      await this.updateSetup();
    } finally {
      this.isLoading = false;
    }
  }

  public async updateSetup() {
    if (this.isNewDestination) return;

    this.payoutDestinationDB = await this.getPayoutDestination();

    if (this.payoutDestination) {
      this.form.id = this.payoutDestinationDB?.id;
      this.form.name = this.payoutDestinationDB?.name;
      this.form.default = this.payoutDestinationDB?.default;
      this.form.visible_for_clients = this.payoutDestinationDB?.visible_for_clients;
      this.form = { ...this.form, ...this.payoutDestinationDB?.meta };
      this.form.code = this.payoutDestinationDB?.code;
    }
    this.formClone = _.cloneDeep(this.form);
  }

  public getPayoutDestination() {
    return this.$store.dispatch(
      `data/${DataModules.AFFILIATES_PAYOUT_DESTINATIONS}/get`,
      {
        id: this.payoutDestination?.id,
        brandId: this.brandId,
        storeData: false
      }
    );
  }

  public getAvailablePayoutDestinations() {
    return this.$store.dispatch(
      `data/${DataModules.AFFILIATES_PAYOUT_DESTINATIONS}/availablePayoutDestinations`,
      {
        storeData: false
      }
    );
  }

  public async validateBeforeSubmit() {
    if (this.isDisabled) return;

    try {
      if (!(await this.validate())) {
        throw this.$t("_sentence.form_is_not_valid");
      }

      this.isProcessing = true;

      if (!this.isNewDestination) {
        await this.updatePayoutDestination(
          this.payoutDestination.id,
          this.form
        );
        this.$store.dispatch("toast/show", {
          message: this.$t(
            "_sentence.affiliate.payout_destination_successfully_updated"
          )
        });
      } else {
        await this.createPayoutDestination(this.form);

        this.$store.dispatch("toast/show", {
          message: this.$t(
            "_sentence.affiliate.payout_destination_successfully_created"
          )
        });
      }

      this.$emit("close");
      this.$emit("success");
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }
}
